import axios from 'axios'
import {
  CSRF_TOKEN_URL,
  GET_USER_URL,
  LOGIN_URL,
  LOGOUT_URL,
  TWO_FA_CHECK_URL,
  UPDATE_USER_URL,
} from '@/modules/auth/endpoints'
import ApiErrorFactory from '@/entities/errors/api_errors/ApiErrorsFactory'
import { LoginDataI } from '@/modules/auth/types/store/UserStoreI'
import { UserCheckTwoFAPayloadI, UserUpdatePayloadI } from '@/modules/auth/types/api/UserTypes'
import AxiosFactory from '@/entities/security/AxiosFactory'
import * as Sentry from '@sentry/vue'
import { formatBackupCode } from '@/utils/formatBackupCode'

// The logout call is best-effort: it revokes the session server-side, but the client must never
// wait on it indefinitely, otherwise an expired session leaves the user stuck on a private page.
const LOGOUT_TIMEOUT_MS = 8000

export default class LoginApi {
  static locks = {
    getUser: false,
  }
  async postLogin(data: LoginDataI) {
    try {
      if (data.backupCode) {
        data.backupCode = formatBackupCode(data.backupCode)
      }
      const axiosI = axios.create({
        withCredentials: true,
        baseURL: import.meta.env.VITE_BASE_URL,
      })
      await axiosI.get(CSRF_TOKEN_URL)
      const responseLogin = await axiosI.post(LOGIN_URL, data)
      Sentry.setTag('sessionId', crypto.randomUUID())
      return responseLogin
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async postLogout() {
    try {
      const responseLogout = await AxiosFactory.createAxiosInstanceWithoutToken().get(LOGOUT_URL, {
        timeout: LOGOUT_TIMEOUT_MS,
      })
      return responseLogout
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async postLogoutWithToken() {
    try {
      const responseLogout = await AxiosFactory.createAxiosInstance().get(LOGOUT_URL, {
        timeout: LOGOUT_TIMEOUT_MS,
      })
      return responseLogout
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async getUser() {
    if (LoginApi.locks.getUser) return
    LoginApi.locks.getUser = true
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_URL)
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    } finally {
      LoginApi.locks.getUser = false
    }
  }
  async updateUser(payload: UserUpdatePayloadI) {
    try {
      const response = await AxiosFactory.createAxiosInstance().put(UPDATE_USER_URL, payload)
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async checkTwoFA(payload: UserCheckTwoFAPayloadI) {
    try {
      const response = await AxiosFactory.createAxiosInstanceWithoutToken().post(TWO_FA_CHECK_URL, payload)
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
}
